import { MAX_CRACKLES, layerOf, type VfxDefinition } from "../vfx/definition.ts";
import type { VfxFeed } from "../vfx/feed.ts";
import type { VfxRegistry } from "../vfx/registry.ts";
import { barState, crackleState, newBarState, newCrackleState } from "../vfx/state.ts";

/**
 * The XP bar's vfx (P2.4-T53, ADR-134): the vfx the page asked for on the bar (`xpBarSurge`), played onto the bar's
 * elements. The bar is part of the HUD's DOM, so it is drawn there and not on the canvas: a wave band running along it, a
 * wash over it that pulses in steps, and small sparks crackling off its top edge. Every position is a whole number of
 * logical pixels and every strength a step, so nothing is smeared or faded. The controller holds no element; it writes
 * through `XpBarFxDom`, which the bar component backs with its own spans, so it is tested without a document.
 */

/**
 * What the controller writes to: the bar's spans, each hidden or set.
 */
export interface XpBarFxDom {
  /**
   * Shows the wave at a place, or hides it.
   *
   * @param left The band's left edge across the bar's inner width in logical pixels, never outside it; null to hide.
   * @param width The band's width in logical pixels, narrowed while it enters or leaves so its right edge stays inside the bar too.
   */
  wave(left: number | null, width: number): void;
  /**
   * Sets the pulse's step, 0 for none.
   *
   * @param step The step from 0 to `PULSE_STEPS`.
   */
  pulse(step: number): void;
  /**
   * Shows crackle `index` at a place over the bar, or hides it.
   *
   * @param index The crackle, 0 to `MAX_CRACKLES - 1`.
   * @param left Across the bar in logical pixels, null to hide.
   * @param top Down from the bar's top edge in logical pixels, negative above.
   * @param light Whether it is the light tone.
   * @param size Its width and height in logical pixels.
   */
  crackle(index: number, left: number | null, top: number, light: boolean, size: number): void;
}

/**
 * The controller of one bar.
 *
 * @example
 * ```ts
 * const fx = new XpBarFx(PAGE_XP_BAR_FEED, VFX_REGISTRY, () => PAGE_MOTION.reduced(), () => performance.now(), dom);
 * const running = fx.frame(barWidth);
 * ```
 */
export class XpBarFx {
  private definition: VfxDefinition | null = null;
  private startedAt = 0;
  private readonly state = newBarState();
  private readonly crackle = newCrackleState();
  private shownCrackles = 0;

  /**
   * @param feed The page's feed of vfx on the bar.
   * @param registry The definitions the feed's indices name.
   * @param reduced Whether motion is reduced now.
   * @param now The time in ms the vfx are counted on.
   * @param dom Where the bar's state is written.
   */
  public constructor(
    private readonly feed: VfxFeed,
    private readonly registry: VfxRegistry,
    private readonly reduced: () => boolean,
    private readonly now: () => number,
    private readonly dom: XpBarFxDom,
  ) {}

  /**
   * Takes the vfx asked for since the last call, starting the last of them from now, and writes the bar's state for now.
   *
   * @param width The bar's inner width in logical pixels, which the wave and the crackles are placed on.
   * @returns True while a vfx runs, false once it is over and the bar is clear, so the caller can stop asking.
   */
  public frame(width: number): boolean {
    this.feed.take((_handle, key) => {
      const definition = this.registry.at(key);
      if (definition !== undefined && definition.target === "xpBar") {
        this.definition = definition;
        this.startedAt = this.now();
      }
    });
    const definition = this.definition;
    if (definition === null) {
      return false;
    }
    if (!barState(definition, this.now() - this.startedAt, this.reduced(), this.state)) {
      this.definition = null;
      this.clear();
      return false;
    }
    this.apply(definition, width);
    return true;
  }

  /**
   * Ends whatever runs and clears the bar, as the bar's removal or a session's end must.
   */
  public stop(): void {
    this.definition = null;
    this.clear();
  }

  /**
   * Writes the state to the bar's spans.
   *
   * @param definition The running vfx.
   * @param width The bar's inner width.
   */
  private apply(definition: VfxDefinition, width: number): void {
    const state = this.state;
    if (state.wavePos < 0) {
      this.dom.wave(null, 0);
    } else {
      const travel = width + state.waveWidth;
      const left = Math.round(state.wavePos * travel) - state.waveWidth;
      const from = Math.max(0, left);
      const to = Math.min(width, left + state.waveWidth);
      if (to > from) {
        this.dom.wave(from, to - from);
      } else {
        this.dom.wave(null, 0);
      }
    }
    this.dom.pulse(state.pulseStep);
    const layer = layerOf(definition, "crackle");
    let shown = 0;
    for (let index = 0; index < MAX_CRACKLES; index += 1) {
      if (layer !== undefined && state.crackleClock >= 0 && crackleState(layer, index, state.crackleClock, this.crackle)) {
        this.dom.crackle(index, Math.round(this.crackle.at * width) + this.crackle.dx, this.crackle.dy, this.crackle.light === 1, this.crackle.size);
        shown = index + 1;
      } else if (index < this.shownCrackles) {
        this.dom.crackle(index, null, 0, false, 1);
      }
    }
    this.shownCrackles = shown;
  }

  /**
   * Hides every part.
   */
  private clear(): void {
    this.dom.wave(null, 0);
    this.dom.pulse(0);
    for (let index = 0; index < this.shownCrackles; index += 1) {
      this.dom.crackle(index, null, 0, false, 1);
    }
    this.shownCrackles = 0;
  }
}
